import {hcl, rgb} from 'd3-color';

type TransferFunctionValues = {
  slope: number;
  srgb: number;
  linear: number;
};

type Standard = 'iec' | 'wcag' | 'preciseIec' | 'preciseWcag';

// There are multiple standards that describe the thresholds of the sRGB
// transfer function. They differ slightly, and are described below.
//
// [0] https://webstore.iec.ch/publication/6169
// [1] https://en.wikipedia.org/wiki/SRGB#Specification_of_the_transformation
// [2] http://www.color.org/srgb.pdf
// [3] https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
// [4] https://www.w3.org/Graphics/Color/sRGB
// [5] http://entropymine.com/imageworsener/srgbformula/
const sRgbValuesByStandard: Record<Standard, TransferFunctionValues> = {
  // The most common thresholds used for the sRGB transfer function.
  // As specified by the IEC in IEC 61966-2-1:1999 [0].
  // Specified in detail by the sRGB Wikipedia entry [1] and the ICC [2].
  iec: {
    slope: 12.92,
    srgb: 0.04045,
    linear: 0.0031308,
  },
  // The original thresholds used for the sRGB transfer function.
  //
  // As specified by WCAG [3] which references the original W3 sRGB proposal [4],
  // published in 1996.
  //
  // Unfortunately, the rounding of these thresholds creates a small
  // discontinuity in the sRGB gamma curve [1][5]. The IEC thresholds are
  // techincally more accurate, but some continue to use these definitions.
  wcag: {
    slope: 12.92,
    srgb: 0.03928,
    linear: 0.00304,
  },
  // Non-standard.
  //
  // The rounding in the IEC definitions still create a very slight
  // discontinuity in the gamma curve. These unrounded values close the gap.[5]
  preciseIec: {
    slope: 12.92,
    srgb: 0.0404482362771082,
    linear: 0.00313066844250063,
  },
  // Non-standard.
  //
  // The rounding in the WCAG definitions create a discontinuity in the gamma
  // curve. These unrounded values close the gap by changing the slope.[1]
  preciseWcag: {
    slope: 12.9232102,
    srgb: 0.0392857,
    linear: 0.00303993,
  },
};

function toHcl(str: string): HCL {
  return hcl(str) as unknown as HCL;
}

function toRgb(str: string): RGB {
  return rgb(str) as unknown as RGB;
}

const sRgbToLinearRgb = (standard: Standard, value: number): number => {
  const {slope, srgb} = sRgbValuesByStandard[standard];
  if (value <= srgb) {
    return value / slope;
  }
  return ((value + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (standard: Standard, color: string): number => {
  const rgb = toRgb(color);
  const r = sRgbToLinearRgb(standard, rgb.r / 255);
  const g = sRgbToLinearRgb(standard, rgb.g / 255);
  const b = sRgbToLinearRgb(standard, rgb.b / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const wcagRelativeLuminance: (arg1: string) => number = (color: string) =>
  relativeLuminance('wcag', color);

// https://www.w3.org/TR/WCAG20/Overview.html#contrast-ratiodef
const wcagContrastRatio = (a: string, b: string): number => {
  const aL = wcagRelativeLuminance(a);
  const bL = wcagRelativeLuminance(b);
  const minL = Math.min(aL, bL);
  const maxL = Math.max(aL, bL);
  return (maxL + 0.05) / (minL + 0.05);
};

// https://www.w3.org/TR/WCAG20/Overview.html#visual-audio-contrast-contrast
const wcagContrastRatioThreshold = {
  AALarge: 3,
  AASmall: 4.5,
  AAALarge: 4.5,
  AAASmall: 7,
} as const;

type WcagComplianceLevel = 'AA' | 'AAA';
type WcagContrastRatioOptions = {
  fontSize: number;
  weight?: number | 'light' | 'regular' | 'medium' | 'bold';
  level?: WcagComplianceLevel;
};
type WcagContrastRatioValidator = (arg1: string, arg2: string) => boolean;

const Tint = 1;
const Shade = -1;
type LightnessSign = typeof Tint | typeof Shade;

type HCL = string & {
  h: number;
  c: number;
  l: number;
  opacity: number;
  displayable: () => boolean;
  hex: () => string;
};

type RGB = string & {
  r: number;
  g: number;
  b: number;
  opacity: number;
  displayable: () => boolean;
  hex: () => string;
};

const contrastRatioValidator =
  ({
    fontSize,
    weight = 'regular',
    level = 'AA',
  }: WcagContrastRatioOptions): WcagContrastRatioValidator =>
  (a: string, b: string) => {
    const ratio = wcagContrastRatio(a, b);
    const {AALarge, AASmall, AAALarge, AAASmall} = wcagContrastRatioThreshold;

    const isAAA = level === 'AAA';
    const isBold =
      weight === 'medium' ||
      weight === 'bold' ||
      (typeof weight === 'number' && weight >= 500);

    const largeFontSizeThreshold = isBold ? 14 : 18;

    let ratioThreshold: 3 | 4.5 | 7 = isAAA ? AAASmall : AASmall;
    if (fontSize >= largeFontSizeThreshold) {
      ratioThreshold = isAAA ? AAALarge : AALarge;
    }

    return ratio >= ratioThreshold;
  };

const white = toHcl('#ffffff');
const black = toHcl('#000000');

const defaultValidator = contrastRatioValidator({fontSize: 14, weight: 500});

export default function getLightnessSign(
  color: string,
  prefer: LightnessSign = Tint,
  isContrastValid: WcagContrastRatioValidator = defaultValidator,
): LightnessSign {
  const first = prefer === Tint ? white : black;
  if (isContrastValid(color, first)) {
    return prefer;
  }

  const second = prefer === Tint ? black : white;
  if (isContrastValid(color, second)) {
    return prefer === Tint ? Shade : Tint;
  }

  return wcagContrastRatio(color, white) >= wcagContrastRatio(color, black)
    ? Tint
    : Shade;
}
